import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import * as qzPrint from '../../utils/qzPrint';

/**
 * Pack Station view for a ship-from-store box (market_shipments ticket).
 *
 * The packer scans the QR on the store's bag slip (the Run check-in URL
 * ...?checkin=SHIP-0012) or types SHIP-0012, exactly where a sales order is
 * scanned. The box arrives packed from the store and the sale already posted
 * to NetSuite as a cash sale at the store location, so there is no item scan
 * and no item fulfillment: check it in, weigh it, buy the UPS label through
 * /api/market-shipments, print it on this station's Zebra (network, then QZ
 * Tray, the same order the pack station uses for sales orders) and mark it
 * shipped. The customer gets the tracking email from the label route.
 */
export function storeTicketFrom(value) {
  const m = String(value || '').match(/SHIP-\d+/i);
  return m ? m[0].toUpperCase() : null;
}

function downloadZpl(labelData, name) {
  const blob = new Blob([atob(labelData)], { type: 'text/plain' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `${name}.zpl`;
  a.click();
  URL.revokeObjectURL(a.href);
}

export default function StoreShipmentPack({ shipment: initial, printerIp, qzConnected, qzSelectedPrinter, onDone, sounds }) {
  const [shipment, setShipment] = useState(initial);
  const [weight, setWeight] = useState(initial.package_weight_lbs ? String(initial.package_weight_lbs) : '');
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [result, setResult] = useState(null); // { trackingNumber, printed, labelData }
  const weightRef = useRef(null);

  useEffect(() => { if (weightRef.current) weightRef.current.focus(); }, []);

  const s = shipment;
  const items = Array.isArray(s.items) ? s.items : [];
  const fail = (msg) => { setError(msg); if (sounds?.error) sounds.error(); };

  // Network Zebra, then QZ Tray, then download the .zpl: the shipping PCs
  // print a downloaded .zpl straight to the Zebra, so that is a print too.
  const printLabel = async (labelData) => {
    if (printerIp) {
      try {
        const r = await axios.post('/api/print/zpl', { zpl: atob(labelData), printerIp });
        if (r.data && r.data.success !== false) return true;
      } catch (err) {
        console.error('Network print failed:', err.message);
      }
    }
    if (qzConnected) {
      try {
        await qzPrint.printZPL(atob(labelData), qzSelectedPrinter || null);
        return true;
      } catch (err) {
        console.error('QZ print failed:', err.message);
      }
    }
    try {
      downloadZpl(labelData, s.ticket_number);
      return true;
    } catch (err) {
      console.error('Label download failed:', err.message);
    }
    return false;
  };

  const finish = async (labelData, trackingNumber) => {
    const printed = await printLabel(labelData);
    try {
      const r = await axios.post(`/api/market-shipments/${s.id}/shipped`);
      if (r.data?.shipment) setShipment(r.data.shipment);
    } catch (err) {
      console.error('Mark shipped failed:', err.response?.data?.error || err.message);
    }
    setResult({ trackingNumber, printed, labelData });
    if (sounds?.complete) sounds.complete();
  };

  const shipIt = async () => {
    setError('');
    const w = Number(weight);
    if (!Number.isFinite(w) || w <= 0 || w > 150) return fail('Enter the box weight in pounds.');
    try {
      setBusy('Checking in');
      try { await axios.post(`/api/market-shipments/${s.id}/received`); } catch { /* check-in is a nicety */ }
      setBusy('Buying UPS label');
      const r = await axios.post(`/api/market-shipments/${s.id}/label`, { weight: w });
      if (r.data?.shipment) setShipment(r.data.shipment);
      setBusy('Printing label');
      await finish(r.data.labelData, r.data.trackingNumber);
    } catch (err) {
      const d = err.response?.data || {};
      // Label bought but not saved: still print it.
      if (d.labelData) await finish(d.labelData, d.trackingNumber);
      fail(d.error || err.message);
    } finally {
      setBusy('');
    }
  };

  const reprint = async () => {
    setError('');
    try {
      setBusy('Printing label');
      const r = await axios.get(`/api/market-shipments/${s.id}/label`);
      if (s.status === 'label_created') await finish(r.data.labelData, r.data.trackingNumber);
      else setResult({ trackingNumber: r.data.trackingNumber, printed: await printLabel(r.data.labelData), labelData: r.data.labelData });
    } catch (err) {
      fail(err.response?.data?.error || err.message);
    } finally {
      setBusy('');
    }
  };

  let blocked = null;
  if (s.status === 'awaiting_info') blocked = 'The customer has not entered their address yet. Set this box aside; the store has the ticket.';
  else if (s.status === 'cancelled') blocked = 'This ticket is cancelled. Do not ship it; ask the store.';

  const box = { background: '#fff', border: '1px solid #e5e0d8', borderRadius: 12, padding: 20, marginBottom: 16 };
  const btn = { padding: '14px 26px', fontSize: 20, fontWeight: 700, borderRadius: 10, border: 'none', cursor: 'pointer', background: '#8B6F4E', color: '#fff' };
  const ghost = { ...btn, background: '#fff', color: '#3d3630', border: '1px solid #d8d0c4' };

  return (
    <div className="pack-station" style={{ background: '#FAFAF8', minHeight: '100vh', padding: 24 }}>
      <div style={{ maxWidth: 820, margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 16 }}>
          <h1 style={{ margin: 0, fontSize: 28 }}>Store shipment {s.ticket_number}</h1>
          <button type="button" style={ghost} onClick={onDone}>Back</button>
        </div>

        <div style={box}>
          <div style={{ fontSize: 13, color: '#6b625a', textTransform: 'uppercase', letterSpacing: 1 }}>Ship to</div>
          <div style={{ fontSize: 22, fontWeight: 700, marginTop: 4 }}>{s.customer_name || 'No name yet'}</div>
          <div style={{ fontSize: 18 }}>{s.address1}{s.address2 ? `, ${s.address2}` : ''}</div>
          <div style={{ fontSize: 18 }}>{[s.city, s.state, s.zip].filter(Boolean).join(', ')}</div>
          <div style={{ marginTop: 10, color: '#6b625a' }}>
            Sale {s.sale_number || '-'}{s.checkout_notes ? ` · ${s.checkout_notes}` : ''}
          </div>
          {items.length > 0 && (
            <ul style={{ margin: '10px 0 0', paddingLeft: 20 }}>
              {items.map((it, i) => <li key={i}>{it.quantity} x {it.title || it.sku}{it.sku ? ` (${it.sku})` : ''}</li>)}
            </ul>
          )}
        </div>

        {blocked && <div style={{ ...box, borderColor: '#efc9c4', background: '#fbeeec', color: '#a13a2f', fontWeight: 700, fontSize: 18 }}>{blocked}</div>}

        {!blocked && !result && s.status === 'ready' && (
          <div style={box}>
            <label style={{ display: 'block', fontSize: 18, fontWeight: 600, marginBottom: 8 }}>Box weight (lbs)</label>
            <div style={{ display: 'flex', gap: 12 }}>
              <input
                ref={weightRef}
                type="number"
                step="0.1"
                min="0"
                value={weight}
                onChange={(e) => setWeight(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !busy) shipIt(); }}
                style={{ flex: 1, fontSize: 26, padding: '12px 14px', borderRadius: 10, border: '1px solid #d8d0c4' }}
              />
              <button type="button" style={btn} disabled={!!busy} onClick={shipIt}>{busy || 'Ship It'}</button>
            </div>
            <div style={{ marginTop: 8, color: '#6b625a' }}>UPS Ground, shipped as Candlefish. Prints on this station's label printer and emails the customer the tracking number.</div>
          </div>
        )}

        {!blocked && !result && (s.status === 'label_created' || s.status === 'shipped') && (
          <div style={box}>
            <div style={{ fontSize: 18, marginBottom: 10 }}>
              {s.status === 'shipped' ? 'Already shipped' : 'Label already bought'}{s.tracking_number ? `: ${s.tracking_number}` : ''}.
            </div>
            <button type="button" style={btn} disabled={!!busy} onClick={reprint}>{busy || (s.status === 'shipped' ? 'Reprint label' : 'Print label and mark shipped')}</button>
          </div>
        )}

        {result && (
          <div style={{ ...box, borderColor: '#b9dcbf', background: '#f3faf4' }}>
            <div style={{ fontSize: 24, fontWeight: 700, color: '#2f6b3a' }}>Shipped</div>
            <div style={{ fontSize: 18, marginTop: 6 }}>Tracking {result.trackingNumber}</div>
            {!result.printed && <div style={{ marginTop: 12, color: '#a13a2f', fontWeight: 600 }}>The label did not print.</div>}
            <div style={{ marginTop: 12 }}>
              <button type="button" style={ghost} onClick={() => downloadZpl(result.labelData, s.ticket_number)}>Print label again</button>
            </div>
            <div style={{ marginTop: 14 }}>
              <button type="button" style={btn} onClick={onDone}>Next order</button>
            </div>
          </div>
        )}

        {error && <div style={{ ...box, borderColor: '#efc9c4', background: '#fbeeec', color: '#a13a2f', fontWeight: 600 }}>{error}</div>}
      </div>
    </div>
  );
}
